import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Shield, Pencil, Trash2, Plus, KeyRound, Mail, User, Camera, RotateCcw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '@/lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminAccountsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '' });
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
  const [resetForm, setResetForm] = useState({ email: '', master_key: '', new_password: '' });

  // iter91: Snapshot profilo utente
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [snapshotsTarget, setSnapshotsTarget] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/accounts`, { withCredentials: true });
      setAdmins(res.data);
    } catch (err) {
      console.error('Errore caricamento admin accounts:', err);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const openEdit = (admin) => {
    setEditAdmin(admin);
    setEditForm({ name: admin.name, email: admin.email, password: '' });
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      const data = { name: editForm.name, email: editForm.email };
      if (editForm.password) data.password = editForm.password;
      await axios.put(`${API}/admin/accounts/${editAdmin.id}`, data, { withCredentials: true });
      toast.success('Account aggiornato');
      setEditOpen(false);
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password || !createForm.name) { toast.error('Compila tutti i campi'); return; }
    try {
      await axios.post(`${API}/auth/admin-create-user`, { ...createForm, role: 'admin' }, { withCredentials: true });
      toast.success('Admin creato');
      setCreateOpen(false);
      setCreateForm({ name: '', email: '', password: '' });
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Eliminare l'account admin ${admin.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/accounts/${admin.id}`, { withCredentials: true });
      toast.success('Account eliminato');
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleToggle = async (admin) => {
    try {
      await axios.put(`${API}/admin/accounts/${admin.id}`, { status: admin.status === 'attivo' ? 'sospeso' : 'attivo' }, { withCredentials: true });
      toast.success(`${admin.name} ${admin.status === 'attivo' ? 'disabilitato' : 'abilitato'}`);
      fetchAdmins();
    } catch (err) { toast.error('Errore'); }
  };

  const handleReset = async () => {
    try {
      const res = await axios.post(`${API}/auth/reset-admin-password`, resetForm);
      toast.success(res.data.message);
      setResetOpen(false);
      setResetForm({ email: '', master_key: '', new_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  // iter91: Snapshot profilo utente
  const openSnapshots = async (admin) => {
    setSnapshotsTarget(admin);
    setSnapshotsOpen(true);
    setSnapshotsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${admin.id}/snapshots`, { withCredentials: true });
      setSnapshots(r.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento snapshot');
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const createSnapshot = async () => {
    if (!snapshotsTarget) return;
    try {
      await axios.post(`${API}/admin/users/${snapshotsTarget.id}/snapshots`, {}, { withCredentials: true });
      toast.success('Snapshot creato');
      openSnapshots(snapshotsTarget);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const restoreSnapshot = async (snap) => {
    if (!snapshotsTarget) return;
    if (!window.confirm(`Ripristinare ${snapshotsTarget.name} allo stato del ${fmtDate(snap.created_at, 'dd-MM-yyyy HH:mm')}?\n\nVerrà creato uno snapshot dello stato attuale prima del ripristino.`)) return;
    try {
      await axios.post(`${API}/admin/users/${snapshotsTarget.id}/snapshots/${snap.id}/restore`, {}, { withCredentials: true });
      toast.success('Profilo utente ripristinato — dì all\'utente di ricaricare l\'app');
      openSnapshots(snapshotsTarget);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const triggerLabel = (t) => {
    if (!t) return 'manuale';
    if (t === 'manual') return 'Manuale';
    if (t === 'auto_nav_layout_update') return 'Auto: aggiornamento menu';
    if (t?.startsWith('pre_restore_')) return 'Auto: prima di un ripristino';
    return t;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="admin-accounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Account Admin</h1>
          <p className="text-slate-500 mt-1">Gestisci gli account amministratore</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="text-amber-700 border-amber-200" onClick={() => setResetOpen(true)} data-testid="reset-password-btn">
            <KeyRound className="h-4 w-4 mr-2" />Reset Password
          </Button>
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setCreateOpen(true)} data-testid="create-admin-btn">
            <Plus className="h-4 w-4 mr-2" />Nuovo Admin
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Nome</TableHead>
                <TableHead className="text-xs uppercase">Email</TableHead>
                <TableHead className="text-xs uppercase">Stato</TableHead>
                <TableHead className="text-xs uppercase text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map(admin => (
                <TableRow key={admin.id} className={`border-slate-100 ${admin.status === 'sospeso' ? 'opacity-50' : ''}`}>
                  <TableCell className="font-medium flex items-center gap-2"><Shield className="h-4 w-4 text-blue-600" />{admin.name}</TableCell>
                  <TableCell className="text-slate-600">{admin.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={admin.status === 'attivo'} onCheckedChange={() => handleToggle(admin)} />
                      <Badge variant="outline" className={admin.status === 'attivo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}>{admin.status === 'attivo' ? 'Attivo' : 'Sospeso'}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openSnapshots(admin)} title="Snapshot profilo (backup e ripristino)" data-testid={`snapshot-btn-${admin.id}`}><Camera className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(admin)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(admin)}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Modifica Admin</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label>Nome</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} /></div>
            <div><Label>Password (lascia vuoto per non cambiare)</Label><Input type="password" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} placeholder="Nuova password" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button><Button onClick={handleSave} className="bg-slate-900 text-white">Salva</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Nuovo Admin</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label>Nome</Label><Input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder="Nome e Cognome" /></div>
            <div><Label>Email</Label><Input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} placeholder="email@azienda.it" /></div>
            <div><Label>Password</Label><Input type="password" value={createForm.password} onChange={e => setCreateForm({...createForm, password: e.target.value})} placeholder="Min. 6 caratteri" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button><Button onClick={handleCreate} className="bg-slate-900 text-white">Crea Admin</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-700"><KeyRound className="h-5 w-5" />Reset Password Admin</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Usa la chiave master per resettare la password di un admin. Funziona anche senza essere loggati.</p>
          <div className="space-y-4 mt-2">
            <div><Label>Email Admin</Label><Input type="email" value={resetForm.email} onChange={e => setResetForm({...resetForm, email: e.target.value})} placeholder="email@azienda.it" /></div>
            <div><Label>Chiave Master</Label><Input type="password" value={resetForm.master_key} onChange={e => setResetForm({...resetForm, master_key: e.target.value})} placeholder="Chiave di sicurezza" /></div>
            <div><Label>Nuova Password</Label><Input type="password" value={resetForm.new_password} onChange={e => setResetForm({...resetForm, new_password: e.target.value})} placeholder="Nuova password" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setResetOpen(false)}>Annulla</Button><Button onClick={handleReset} className="bg-amber-600 text-white hover:bg-amber-700">Reset Password</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter91: Snapshot Profilo Utente */}
      <Dialog open={snapshotsOpen} onOpenChange={setSnapshotsOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="snapshots-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-blue-600" />
              Snapshot profilo — {snapshotsTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Ogni snapshot salva menu personalizzato, permessi, layout dashboard e notifiche.
            Uno snapshot viene creato <b>automaticamente</b> prima di ogni modifica al menu.
            Puoi <b>ripristinare</b> in caso di corruzione (schermate blu, menu rotto, ecc.).
          </p>
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={createSnapshot} className="bg-slate-900 text-white" data-testid="create-snapshot-btn">
              <Camera className="h-3.5 w-3.5 mr-1.5" />Crea snapshot ora
            </Button>
          </div>
          <div className="mt-3 border rounded-lg divide-y max-h-[420px] overflow-y-auto" data-testid="snapshots-list">
            {snapshotsLoading ? (
              <div className="p-4 text-center text-sm text-slate-400">Caricamento...</div>
            ) : snapshots.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                <Camera className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                Nessuno snapshot ancora — verrà creato al prossimo aggiornamento del menu, o cliccando &quot;Crea snapshot ora&quot;
              </div>
            ) : snapshots.map((snap) => (
              <div key={snap.id} className="p-3 flex items-center gap-3" data-testid={`snapshot-row-${snap.id}`}>
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Clock className="h-4 w-4 text-blue-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900">
                    {fmtDate(snap.created_at, 'dd-MM-yyyy HH:mm:ss')}
                  </div>
                  <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span>{triggerLabel(snap.trigger)}</span>
                    {snap.admin_who_triggered && <span>· da {snap.admin_who_triggered}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => restoreSnapshot(snap)}
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid={`restore-snapshot-${snap.id}`}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Ripristina
                </Button>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setSnapshotsOpen(false)}>Chiudi</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
